import { useState } from 'react'
import type { Channel } from '@/api/channels'

interface Props {
  channel: Channel | null
  submitting: boolean
  error: string | null
  onSubmit: (checkboxes: [boolean, boolean, boolean]) => void
  onBack: () => void
}

/**
 * Шаг 2 активации — Согласие.
 *
 * Один чекбокс (после ревизии с Виктором 16.06.2026): согласие на условия
 * публикации в Витрине + подтверждение авторских прав. Условия по ссылке —
 * https://telegraphyx.ru/reklamasoglasio/ (соглашение на публикацию).
 *
 * + URL preview + зелёная плашка про «убрать в один клик».
 *
 * Это юридически значимое согласие — бэк сохраняет timestamp, версию условий,
 * email/maxAdminID юзера. Для совместимости с существующим контрактом
 * activateConsent ([bool, bool, bool]) при checked отправляем [true, true, true].
 */
export function StepConsent({ channel, submitting, error, onSubmit, onBack }: Props) {
  const [checked, setChecked] = useState(false)

  // Превью URL — используем maxUsername канала (slug) если есть, fallback на
  // maxChannelID. По договорённости 16.06 портал должен поддержать оба формата;
  // на фронте показываем более «человеческий» slug чтобы юзер не пугался ID.
  const previewSlug =
    (channel as Channel & { maxUsername?: string | null })?.maxUsername
    ?? channel?.maxChannelID
    ?? 'your-channel'
  const previewUrl = `telegraphyx.ru/channels/${previewSlug}`

  function handleSubmit() {
    if (!checked || submitting) return
    onSubmit([true, true, true])
  }

  return (
    <div className="step-consent">
      <div className="consent-header">
        <h2 className="consent-title">Согласие на&nbsp;публикацию</h2>
        <p className="consent-sub">
          Вы — владелец канала и&nbsp;соглашаетесь публиковать его посты на&nbsp;нашем портале. Это нужно для
          поисковиков — они индексируют страницы и&nbsp;направляют читателей.
        </p>
      </div>

      <div className="consent-card">
        <label className={`consent-row ${checked ? 'on' : ''}`}>
          <input type="checkbox" checked={checked} onChange={(e) => setChecked(e.target.checked)} />
          <span className="consent-box">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </span>
          <span className="consent-text">
            Согласен с&nbsp;
            <a
              href="https://telegraphyx.ru/reklamasoglasio/"
              target="_blank"
              rel="noopener noreferrer"
            >
              условиями публикации в&nbsp;Витрине
            </a>
            {' '}и&nbsp;подтверждаю, что&nbsp;обладаю правами на&nbsp;контент канала.
          </span>
        </label>
      </div>

      <div className="consent-preview">
        <div className="preview-eyebrow">Адрес ваших страниц</div>
        <div className="preview-url">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="18" height="18" rx="3" />
            <path d="M3 9h18M9 3v18" />
          </svg>
          <span className="preview-host">{previewUrl}</span>
          <span className="preview-slug">/название-поста</span>
        </div>
      </div>

      <div className="consent-note">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6L9 17l-5-5" />
        </svg>
        <span>
          <b>Любой пост можно убрать из&nbsp;поиска в&nbsp;один клик.</b> Также можно выключить всю Витрину
          в&nbsp;настройках.
        </span>
      </div>

      {error && <div className="consent-error">{error}</div>}

      <div className="consent-actions">
        <button className="cta-back" onClick={onBack} disabled={submitting}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5M11 6l-6 6 6 6" />
          </svg>
          Назад
        </button>
        <button className="cta-primary" onClick={handleSubmit} disabled={!checked || submitting}>
          {submitting ? 'Сохраняем…' : 'Дальше — выбор постов'}
          {!submitting && (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          )}
        </button>
      </div>

      <style>{`
        .step-consent {
          display: flex;
          flex-direction: column;
          gap: 24px;
          max-width: 720px;
          margin: 0 auto;
          width: 100%;
        }
        .consent-header { text-align: center; }
        .consent-title {
          font-size: 24px;
          font-weight: 800;
          margin: 0 0 8px;
          color: var(--tg-ink, #111);
        }
        .consent-sub {
          font-size: 14px;
          color: var(--tg-mute, #5b6678);
          line-height: 1.55;
          margin: 0;
          max-width: 540px;
          margin: 0 auto;
        }
        .consent-card {
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 14px;
          padding: 6px;
          display: flex;
          flex-direction: column;
        }
        .consent-row {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          padding: 14px 16px;
          cursor: pointer;
          border-radius: 10px;
          transition: background 150ms;
        }
        .consent-row:hover { background: var(--tg-paper, #fafafd); }
        .consent-row.on { background: var(--tg-blue-bg, #ebf2ff); }
        .consent-row input[type="checkbox"] {
          position: absolute;
          opacity: 0;
          pointer-events: none;
        }
        .consent-box {
          width: 22px;
          height: 22px;
          flex-shrink: 0;
          margin-top: 1px;
          border-radius: 6px;
          border: 1.5px solid var(--tg-line-3, #d0d0d8);
          background: white;
          display: flex;
          align-items: center;
          justify-content: center;
          color: transparent;
          transition: background 150ms, border-color 150ms;
        }
        .consent-box svg { width: 14px; height: 14px; }
        .consent-row.on .consent-box {
          background: var(--tg-blue, #0066ff);
          border-color: var(--tg-blue, #0066ff);
          color: white;
        }
        .consent-text {
          font-size: 13.5px;
          color: var(--tg-ink-2, #333);
          line-height: 1.5;
        }
        .consent-text b { color: var(--tg-ink, #111); font-weight: 700; }
        .consent-text a {
          color: var(--tg-blue-press, #0052cc);
          text-decoration: underline;
        }
        .consent-preview {
          background: var(--tg-paper, #fafafd);
          border: 1px dashed var(--tg-line-3, #d0d0d8);
          border-radius: 12px;
          padding: 14px 18px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .preview-eyebrow {
          font-size: 11px;
          font-weight: 700;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: var(--tg-mute, #5b6678);
        }
        .preview-url {
          display: flex;
          align-items: center;
          gap: 8px;
          font-family: ui-monospace, SFMono-Regular, monospace;
          font-size: 13px;
        }
        .preview-url svg { width: 16px; height: 16px; color: var(--tg-mute, #5b6678); flex-shrink: 0; }
        .preview-host { color: var(--tg-ink, #111); font-weight: 600; }
        .preview-slug { color: var(--tg-mute, #5b6678); }
        .consent-note {
          display: flex;
          align-items: flex-start;
          gap: 10px;
          padding: 12px 16px;
          background: rgba(0, 168, 96, 0.08);
          border: 1px solid rgba(0, 168, 96, 0.2);
          border-radius: 10px;
          font-size: 13px;
          color: var(--tg-ink-2, #333);
          line-height: 1.5;
        }
        .consent-note svg {
          width: 18px;
          height: 18px;
          color: var(--tg-up, #00a860);
          flex-shrink: 0;
          margin-top: 1px;
        }
        .consent-error {
          padding: 10px 14px;
          background: rgba(255, 107, 107, 0.1);
          border: 1px solid rgba(255, 107, 107, 0.3);
          color: #c53030;
          border-radius: 10px;
          font-size: 13px;
        }
        .consent-actions {
          display: flex;
          justify-content: space-between;
          gap: 10px;
        }
        .cta-back {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 11px 18px;
          background: var(--tg-surface, #fff);
          color: var(--tg-mute, #5b6678);
          font-weight: 600;
          font-size: 13.5px;
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 10px;
          cursor: pointer;
          font-family: inherit;
        }
        .cta-back:hover { background: var(--tg-paper, #fafafd); color: var(--tg-ink, #111); }
        .cta-back:disabled { opacity: 0.5; cursor: not-allowed; }
        .cta-back svg { width: 14px; height: 14px; }
        .cta-primary {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 13px 24px;
          background: var(--tg-blue, #0066ff);
          color: white;
          font-weight: 700;
          font-size: 14px;
          border: 0;
          border-radius: 12px;
          cursor: pointer;
          font-family: inherit;
          transition: background 150ms;
          box-shadow: 0 4px 16px -4px rgba(0,102,255,0.35);
        }
        .cta-primary:hover:not(:disabled) { background: var(--tg-blue-press, #0052cc); }
        .cta-primary:disabled {
          background: var(--tg-line-2, #f0f0f4);
          color: var(--tg-mute, #5b6678);
          cursor: not-allowed;
          box-shadow: none;
        }
        .cta-primary svg { width: 16px; height: 16px; }
      `}</style>
    </div>
  )
}
